// KHỐI CHỌN GÓI & MUA — dựng theo đúng bảng giá công khai (cnvwork.com/pricing).
//
// Dùng ở hai chỗ: màn Hộp thư khi workspace chưa mua gói chat, và Kho ứng dụng. Cùng một khối
// nên khách thấy đúng một cách tính tiền ở mọi cửa — trước đây mỗi chỗ tự dựng giá một kiểu.
//
// ⛔ MỌI CON SỐ ĐẾN TỪ `/marketplace/pricing/:item_key`, tức là từ bảng giá bậc của hệ bán hàng.
// Không chép bảng giá vào đây. Bản chép tay đã có tiền lệ hỏng: `admin/src/marketplace/pricing.js`
// thiếu hẳn bậc 5 user so với website và không ai phát hiện ra cho tới khi đối chiếu thủ công.
//
// ⛔ Và KHÔNG gửi số tiền lên khi đặt đơn — chỉ gửi "mua gì, mấy đơn vị, kỳ hạn nào". Giá hiển
// thị ở đây chỉ để khách nhìn; bên bán hàng chốt lại giá lúc phát hành hoá đơn, và trang đơn
// hàng hiện đúng số đó trước khi khách chuyển khoản.

import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Trans, useLingui } from '@lingui/react/macro';
import { Loader2, ShoppingCart, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '@host/axios';
import { fmtCurrency } from '../../i18n/format.js';
import { defaultCurrency } from '../approvals/builder/fieldRegistry';
import { PRODUCT_NAME } from '../../lib/hostBrand';

/**
 * Kỳ hạn bán. Mã PHẢI khớp `crm_product_pricing_tiers.billing_cycle` bên hệ bán hàng — đặt tên
 * riêng cho đẹp (kiểu '3m') là mỗi tầng phải dịch qua lại, và tầng nào dịch sai thì khách xem
 * giá 3 tháng nhưng nhận hoá đơn 6 tháng.
 */
const CYCLE_MONTHS = { monthly: 1, quarterly: 3, semi_yearly: 6, yearly: 12 };

/**
 * Nhãn đơn vị đọc được cho người dùng.
 *
 * Đơn vị do hệ bán hàng trả về (`page` / `user` / `gb`) KHÔNG đi qua Lingui — in thẳng thì màn
 * tiếng Anh lòi chữ máy, màn tiếng Việt lòi chữ Anh. Để ở ĐÂY, dùng chung mọi màn có nhắc tới
 * đơn vị: hai bản nhãn là hai bản sẽ trôi khỏi nhau (trang mua ghi "Page", trang đơn ghi "page").
 * Đơn vị lạ thì giữ nguyên văn — thà hiện chữ của hệ bán hàng còn hơn nuốt mất.
 */
export function useUnitLabel() {
  const { t } = useLingui();
  return (u) => ({
    page: t`Page`, user: t`người dùng`, gb: t`GB`, unit: t`đơn vị`,
    // Gói bán theo GÓI (vd «Gửi tin nhắn tự động qua Zalo»: 1 gói = 3 chỗ) — cùng khoá dịch với utils/productUnit.
    package: t({ message: 'gói', context: 'đơn vị tính' }),
  }[String(u || '').toLowerCase()] || u || t`đơn vị`);
}

export const marketplaceOrderApi = {
  pricing: async (item_key) => (await api.get(`/marketplace/pricing/${item_key}`)).data.data,
  create: async (body) => (await api.post('/marketplace/orders', body)).data.data,
  list: async () => (await api.get('/marketplace/orders')).data.data,
  get: async (id) => (await api.get(`/marketplace/orders/${id}`)).data.data,
  cancel: async (id) => (await api.post(`/marketplace/orders/${id}/cancel`)).data.data,
};

export default function PlanPurchasePanel({ itemKey, onOrdered, compact = false }) {
  const { t } = useLingui();
  const unitLabel = useUnitLabel();
  const [quantity, setQuantity] = useState(null);
  const [cycle, setCycle] = useState(null);

  const pricing = useQuery({
    queryKey: ['marketplace-pricing', itemKey],
    queryFn: () => marketplaceOrderApi.pricing(itemKey),
    retry: 1,
  });

  // Bậc và kỳ hạn dựng TỪ DỮ LIỆU, không liệt kê cứng: hệ bán hàng thêm bậc 100 Page thì màn
  // này có ngay, không phải sửa code rồi chờ deploy.
  const cycles = useMemo(() => {
    const set = [...new Set((pricing.data?.tiers || []).map((x) => x.billing_cycle))];
    return set.sort((a, b) => (CYCLE_MONTHS[a] || 99) - (CYCLE_MONTHS[b] || 99));
  }, [pricing.data]);

  const activeCycle = cycle || cycles[0] || null;

  const quantities = useMemo(() => {
    const set = (pricing.data?.tiers || [])
      .filter((x) => x.billing_cycle === activeCycle)
      .map((x) => x.quantity);
    return [...new Set(set)].sort((a, b) => a - b);
  }, [pricing.data, activeCycle]);

  const activeQty = quantity != null && quantities.includes(quantity) ? quantity : quantities[0];

  const tier = useMemo(
    () => (pricing.data?.tiers || []).find((x) => x.quantity === activeQty && x.billing_cycle === activeCycle) || null,
    [pricing.data, activeQty, activeCycle],
  );

  // Mức giảm của kỳ 1 NĂM so với kỳ đắt nhất (tính theo tháng) của CÙNG bậc đang chọn — suy từ bảng giá.
  // Trước 15/09/2026 viết cứng "−20%": đúng với Chat đa kênh (80k → 64k) nhưng khung này nay bán cả gói
  // nền, mà All-in-one 10 user là 198k → 150k = −24%, bậc 5 user (không có kỳ quý) 204k → 170k = −17%.
  const yearlySaving = useMemo(() => {
    const rows = (pricing.data?.tiers || []).filter((x) => x.quantity === activeQty);
    const perMonth = (x) => x.unit_price_per_unit_month
      ?? (CYCLE_MONTHS[x.billing_cycle] ? x.total_price / x.quantity / CYCLE_MONTHS[x.billing_cycle] : null);
    const yearly = rows.find((x) => x.billing_cycle === 'yearly');
    const others = rows.filter((x) => x.billing_cycle !== 'yearly').map(perMonth).filter((v) => v > 0);
    if (!yearly || !others.length || !(perMonth(yearly) > 0)) return null;
    const pct = Math.round((1 - perMonth(yearly) / Math.max(...others)) * 100);
    return pct > 0 ? pct : null;
  }, [pricing.data, activeQty]);

  const order = useMutation({
    mutationFn: () => marketplaceOrderApi.create({
      item_key: itemKey, quantity: activeQty, billing_cycle: activeCycle,
    }),
    onSuccess: (data) => onOrdered?.(data),
    // Câu lỗi của máy chủ đã viết cho người đọc (bậc nào đang bán, đơn nào chưa trả tiền) —
    // hiện nguyên văn, đừng thay bằng "Có lỗi xảy ra".
    onError: (e) => toast.error(e?.response?.data?.message || t`Không đặt được đơn, thử lại sau.`),
  });

  if (pricing.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-gray-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> <Trans>Đang tải bảng giá…</Trans>
      </div>
    );
  }

  // Lấy giá không được thì NÓI THẬT. Hiện bảng trống ở đây đọc thành "gói này ngừng bán".
  if (pricing.isError || !pricing.data?.tiers?.length) {
    return (
      <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50 text-sm">
        <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="text-amber-900">
          <div className="font-semibold"><Trans>Chưa tải được bảng giá</Trans></div>
          <div className="text-amber-700 mt-0.5">
            <Trans>Hệ thống bán hàng đang không trả lời. Thử lại sau ít phút hoặc liên hệ đội {PRODUCT_NAME} để được báo giá.</Trans>
          </div>
        </div>
      </div>
    );
  }

  const unit = unitLabel(pricing.data.unit);
  // Giá theo tiền tệ hệ bán hàng trả (`currency` của bảng giá) — trước 12/10/2026 luôn nối « ₫».
  const cur = pricing.data.currency || defaultCurrency();
  const months = CYCLE_MONTHS[activeCycle] || null;
  // Đơn giá mỗi đơn vị mỗi tháng — con số khách so sánh giữa các bậc. Hệ bán hàng đã trả sẵn;
  // không có thì suy từ giá trọn gói (đừng để ô trống, đó là số khách nhìn đầu tiên).
  const perUnitMonth = tier?.unit_price_per_unit_month
    ?? (tier && months ? Math.round(tier.total_price / tier.quantity / months) : null);

  const cycleLabel = (c) => ({
    monthly: t`1 tháng`, quarterly: t`3 tháng`, semi_yearly: t`6 tháng`, yearly: t`1 năm`,
  }[c] || c);

  return (
    <div className="space-y-4">
      {/* Kỳ hạn */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mr-1">
          <Trans>Kỳ hạn</Trans>
        </span>
        {cycles.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCycle(c)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
              activeCycle === c
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            {cycleLabel(c)}
            {c === 'yearly' && yearlySaving && (
              <span className="ml-1.5 text-[10px] font-bold text-pink-600">−{yearlySaving}%</span>
            )}
          </button>
        ))}
      </div>

      {/* Bậc số lượng */}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">
          <Trans>Chọn số {unit}</Trans>
        </div>
        <div className="flex flex-wrap gap-2">
          {quantities.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setQuantity(q)}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition min-w-[86px] ${
                activeQty === q
                  ? 'border-pink-500 bg-pink-50 text-pink-700'
                  : 'border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              {q} {unit}
            </button>
          ))}
        </div>
      </div>

      {/* Số tiền */}
      {tier && (
        <div className="flex flex-wrap items-end justify-between gap-4 p-4 rounded-xl bg-gray-900 text-white">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/50">
              <Trans>Đơn giá bậc đang chọn</Trans>
            </div>
            <div className="mt-1">
              <span className="text-3xl font-extrabold">{fmtCurrency(perUnitMonth, cur)}</span>
              <span className="text-sm text-white/60 ml-1">/{unit}/<Trans>tháng</Trans></span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-white/50">
              <Trans>Tổng {activeQty} {unit} · {cycleLabel(activeCycle)}</Trans>
            </div>
            <div className="mt-1 text-2xl font-extrabold text-pink-300">
              {fmtCurrency(tier.total_price, cur)}
            </div>
          </div>
        </div>
      )}

      {/* Bảng tham chiếu — khách so bậc mà không phải bấm từng cái */}
      {!compact && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[420px]">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="text-left py-2 font-semibold">{unit}</th>
                <th className="text-right py-2 font-semibold"><Trans>Giá / {unit} / tháng</Trans></th>
                <th className="text-right py-2 font-semibold"><Trans>Tổng {cycleLabel(activeCycle)}</Trans></th>
              </tr>
            </thead>
            <tbody>
              {quantities.map((q) => {
                const row = pricing.data.tiers.find((x) => x.quantity === q && x.billing_cycle === activeCycle);
                if (!row) return null;
                const per = row.unit_price_per_unit_month
                  ?? (months ? Math.round(row.total_price / q / months) : null);
                return (
                  <tr key={q} className={`border-b border-gray-100 ${activeQty === q ? 'bg-pink-50' : ''}`}>
                    <td className="py-2 font-bold text-gray-900">{q}</td>
                    <td className="py-2 text-right text-gray-700">{fmtCurrency(per, cur)}</td>
                    <td className="py-2 text-right font-semibold text-pink-600">{fmtCurrency(row.total_price, cur)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Bảng giá lấy từ bản lưu (hệ bán hàng đang lag) → nói rõ, đừng để khách tưởng là giá
          mới nhất rồi thắc mắc khi hoá đơn ra số khác. */}
      {pricing.data.stale && (
        <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Trans>Bảng giá đang hiển thị từ bản lưu gần nhất. Số tiền chính thức sẽ hiện ở bước thanh toán.</Trans>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
        <div className="text-[12px] text-gray-500">
          <Trans>Đặt đơn xong sẽ có mã QR và nội dung chuyển khoản. Gói được cấp sau khi nhận đủ tiền.</Trans>
        </div>
        <button
          type="button"
          disabled={!tier || order.isPending}
          onClick={() => order.mutate()}
          className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-pink-500 to-rose-500 text-white text-sm font-semibold hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 inline-flex items-center gap-2 shadow-sm"
        >
          {order.isPending
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <ShoppingCart className="w-4 h-4" />}
          <Trans>Mua ngay</Trans>
        </button>
      </div>
    </div>
  );
}
